// src/api.js
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const sendEvent = async (token, topic, message) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/events`, {
      token,
      topic,
      message,
    });
    return response.data;
  } catch (error) {
    console.error("Error sending event:", error);
    throw error;
  }
};

export const createTopic = async (token, topic) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/topics`, {
      token,
      topic,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating topic:", error);
    throw error;
  }
};

export const fetchTopics = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/topics`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching topics:", error);
    throw error;
  }
};

export const fetchMessages = async (topicId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/topics/${topicId}/messages`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
};

export const deleteTopic = async (token, topicId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/topics/${topicId}`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.info("Topic not found, skipping deletion");
      return;
    }
    console.error("Error deleting message:", error);
    throw error;
  }
};

export const updateCardDisplayType = async (
  token,
  cardId,
  subtype,
  variant,
  params = {}
) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/cards/${cardId}`, {
      token,
      subtype,
      variant,
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating card display:", error);
    throw error;
  }
};

export const fetchDashboard = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/dashboard`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching dashboard:", error);
    throw error;
  }
};

export const createCard = async (token, topic_name, card_type) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/cards`, {
      token,
      topic_name,
      card_type,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating card:", error);
    throw error;
  }
};

export const deleteCardById = async (token, cardId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/cards/${cardId}`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting card:", error);
    throw error;
  }
};

export const fetchPushNotificationPublicKey = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/push/public_key`);
    return response.data;
  } catch (error) {
    console.error("Error fetching public key:", error);
    throw error;
  }
};

export const checkPushNotificationSubscription = async (deviceId, cardId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/push/check_subscription`,
      {
        params: { device_id: deviceId, card_id: cardId },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription:", error);
    throw error;
  }
};

export const subscribeToPushNotification = async (
  deviceId,
  cardId,
  subscription
) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/push/subscribe`, {
      device_id: deviceId,
      card_id: cardId,
      subscription,
    });
    return response.data;
  } catch (error) {
    console.error("Error subscribing to push notification:", error);
    throw error;
  }
};

export const unsubscribeFromPushNotification = async (deviceId, cardId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/push/unsubscribe`, {
      params: {
        device_id: deviceId,
        card_id: cardId,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error unsubscribing from push notification:", error);
    throw error;
  }
};

/* Account management */
export const firebaseLogin = async (idToken, accountType = "individual") => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/firebase-login`,
      { account_type: accountType },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error logging in with Firebase:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/* Dashboard management */
export const createDashboard = async (name) => {
  try {
    const authToken = localStorage.getItem("token");
    const response = await axios.post(`${API_BASE_URL}/dashboard`, null, {
      params: { name },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error creating dashboard:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchDashboards = async () => {
  try {
    const authToken = localStorage.getItem("token");
    const response = await axios.get(`${API_BASE_URL}/dashboards`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching dashboards:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const updateDashboard = async (token, name) => {
  try {
    const authToken = localStorage.getItem("token");
    const response = await axios.patch(`${API_BASE_URL}/dashboard`, null, {
      params: { token, name },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error updating dashboard:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const restoreDashboard = async (token) => {
  try {
    const authToken = localStorage.getItem("token");
    const response = await axios.post(
      `${API_BASE_URL}/dashboard/restore`,
      null,
      {
        params: { token },
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error restoring dashboard:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const deleteDashboard = async (token) => {
  try {
    const authToken = localStorage.getItem("token");
    const response = await axios.delete(`${API_BASE_URL}/dashboard`, {
      params: { token },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error deleting dashboard:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/* Business account management */
export const addMemberToBusinessAccount = async (member_email) => {
  try {
    const authToken = localStorage.getItem("token");
    const response = await axios.post(
      `${API_BASE_URL}/account/members`,
      { member_email },
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error adding member to business account:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchBusinessAccountMembers = async () => {
  try {
    const authToken = localStorage.getItem("token");
    const response = await axios.get(`${API_BASE_URL}/account/members`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching business account members:",
      error.response?.data || error.message
    );
    throw error;
  }
};
